import { reactive, readonly, onUnmounted } from 'vue'
import type { Router } from 'vue-router'
import type { RNBridgeState, RNToH5Message } from './types'
import { postMessageToRN } from './toRN'

/**
 * 全局响应式状态：仅放 RN 传递的"值型"字段（持续状态供页面直接引用）。
 * 触发型/事件型(appState/backPress 等)不走这里，走 onRNMessage 订阅。
 */
const state = reactive<RNBridgeState>({
  keyboardHeight: 0,
})

/** type → 订阅者集合 */
const subscribers = new Map<string, Set<(msg: RNToH5Message) => void>>()

/** 解析 RN 消息（try/catch + 类型守卫，忽略非 RN postMessage） */
function parseMessage(raw: any): RNToH5Message | null {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!data || typeof data.type !== 'string') return null
    return data as RNToH5Message
  } catch {
    return null
  }
}

/**
 * 注册全局 message 监听（main.ts 启动时调用一次）
 * 兼容 iOS(document) / Android(window)；同一消息可能两端都触发，由订阅者幂等处理。
 * 值型字段实时写入全局 state（页面直接引用），事件型分发给订阅者。
 */
export function initRNListener(): void {
  const handler = (e: MessageEvent) => {
    const raw = e.data
    const msg = parseMessage(raw)
    if (!msg) return
    // 值型：实时更新全局 state（仅 keyboardHeight；appState 走订阅不存值）
    if (msg.type === 'keyboardHeight' && typeof msg.payload === 'number') {
      state.keyboardHeight = msg.payload
    }
    // 事件型：分发给订阅者（appState/backPress 等）
    const subs = subscribers.get(msg.type)
    if (subs) {
      subs.forEach((fn) => {
        try {
          fn(msg)
        } catch (err) {
          console.error('[bridge] subscriber error:', err)
        }
      })
    }
  }

  window.addEventListener('message', handler)
  document.addEventListener('message', handler as EventListener)
}

/** 读取全局 RN 状态（只读响应式；页面用 rn.field 引用，勿解构以免丢响应性） */
export function useRNBridge() {
  return readonly(state)
}

/**
 * 订阅特定 type 的消息，组件卸载时自动解绑。
 * 必须在组件 setup 同步上下文调用（内部用 onUnmounted）。
 * 泛型按 type 字面量自动窄化 msg（消费端无需 as 断言）。
 */
export function onRNMessage<T extends RNToH5Message['type']>(
  type: T,
  fn: (msg: Extract<RNToH5Message, { type: T }>) => void,
): void {
  const unsubscribe = subscribeRNMessage(type, fn)
  onUnmounted(unsubscribe)
}

/**
 * 非组件代码（如 store action、installRouterBridge）使用的订阅变体，返回手动 unsubscribe。
 */
export function subscribeRNMessage<T extends RNToH5Message['type']>(
  type: T,
  fn: (msg: Extract<RNToH5Message, { type: T }>) => void,
): () => void {
  if (!subscribers.has(type)) subscribers.set(type, new Set())
  subscribers.get(type)!.add(fn as (msg: RNToH5Message) => void)
  return () => subscribers.get(type)?.delete(fn as (msg: RNToH5Message) => void)
}

/**
 * 统一的「返回」处理：有 H5 内部历史则 router.back()，栈底则发 popNative 让 RN pop。
 * 供 BackButton 与 backPress（Android 返回键）共用，避免两处硬编码分叉。
 */
export function backOrPopNative(router: Router): void {
  if (window.history.state?.back) router.back()
  else postMessageToRN({ type: 'popNative' })
}

/**
 * 安装与 router 相关的内置消息处理（main.ts 调用一次）
 * - backPress：Android 返回键（RN allow 模式发），有内部历史 router.back()，栈底发 popNative 让原生 pop
 */
export function installRouterBridge(router: Router): void {
  subscribeRNMessage('backPress', () => {
    backOrPopNative(router)
  })
}
